import type { LstTemplate } from '../types';
import type { LinkedIndicatorAssessment } from '../services/ielService';

export interface SwiDrawerContent {
  objetivo: string | null;
  passosVerificacao: string[];
  conformeQuando: string[];
  desvioQuando: string[];
  evidenciaEsperada: string | null;
}

function stripMarkup(raw: string): string {
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractSection(text: string, heading: string): string | null {
  const plain = text.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n');
  const patterns = [
    new RegExp(`\\*\\*${heading}:?\\*\\*\\s*([\\s\\S]*?)(?=\\*\\*|$)`, 'i'),
    new RegExp(`${heading}:?\\s*\\n([\\s\\S]*?)(?=\\n\\*\\*|\\n[A-ZÁÉÍÓÚÂÊÔÃÕÇ][^\\n]*:|$)`, 'i'),
  ];
  for (const re of patterns) {
    const match = plain.match(re);
    if (match?.[1]?.trim()) return stripMarkup(match[1]);
  }
  return null;
}

function parseSteps(block: string): string[] {
  return block
    .split(/\n/)
    .map((line) => line.replace(/^\d+\.\s*/, '').replace(/^[*•\-]\s*/, '').trim())
    .filter((line) => line.length > 2);
}

function splitFreeText(text: string): string[] {
  const cleaned = stripMarkup(text);
  const byLine = cleaned
    .split(/\n|(?:\d+\.\s)/)
    .map((s) => s.trim())
    .filter((s) => s.length > 3);
  if (byLine.length > 1) return byLine.slice(0, 8);
  return cleaned.length > 3 ? [cleaned] : [];
}

function invertConformityItem(item: string): string {
  const trimmed = item.trim();
  if (/^(não|nao|sem|ausência|ausencia|falta)/i.test(trimmed)) return trimmed;
  if (/executad[oa]/i.test(trimmed)) return trimmed.replace(/executad[oa]/i, 'não executado');
  if (/disponíveis?/i.test(trimmed)) return trimmed.replace(/disponíveis?/i, 'indisponíveis');
  if (/tratados?/i.test(trimmed)) return trimmed.replace(/tratados?/i, 'sem tratamento');
  if (/conforme/i.test(trimmed)) return trimmed.replace(/conforme/i, 'fora do padrão');
  return `Não atende: ${trimmed.charAt(0).toLowerCase()}${trimmed.slice(1)}`;
}

function buildIndicatorCriteria(
  assessment: LinkedIndicatorAssessment | null,
): { conforme: string[]; desvio: string[] } {
  if (!assessment) return { conforme: [], desvio: [] };
  const { name, target, gap, trend, unit } = assessment;
  const conforme: string[] = [];
  const desvio: string[] = [];
  const unitSuffix = unit === '%' || !unit ? '%' : ` ${unit}`;

  if (target != null) {
    conforme.push(`${name} na meta ou acima (${target}${unitSuffix})`);
    desvio.push(`${name} abaixo da meta (${target}${unitSuffix})`);
  }

  if (gap != null && gap < 0) {
    desvio.push(`Gap negativo em ${name} (${gap > 0 ? '+' : ''}${gap}${unitSuffix})`);
  }

  if (trend != null && Math.abs(trend) >= 1) {
    if (trend > 0) conforme.push(`Tendência de ${name} estável ou melhorando`);
    else desvio.push(`Tendência de ${name} piorando`);
  }

  return { conforme, desvio };
}

/** Deriva seções SWI a partir dos campos já cadastrados no template. */
export function buildSwiDrawerContent(
  template: LstTemplate,
  indicatorAssessment?: LinkedIndicatorAssessment | null,
): SwiDrawerContent {
  const swiRaw = template.swi_text?.trim() ?? '';
  const checklist = (template.swi_checklist ?? []).map((c) => c.text.trim()).filter(Boolean);

  const objetivoFromSwi = swiRaw ? extractSection(swiRaw, 'Objetivo') : null;
  const objetivo = objetivoFromSwi ?? template.description?.trim() ?? null;

  const passosBlock =
    swiRaw ? extractSection(swiRaw, 'Como verificar') ?? extractSection(swiRaw, 'Como executar') : null;
  let passosVerificacao = passosBlock ? parseSteps(passosBlock) : [];

  if (passosVerificacao.length === 0 && template.swi_mode === 'checklist' && checklist.length > 0) {
    passosVerificacao = checklist;
  } else if (passosVerificacao.length === 0 && swiRaw) {
    passosVerificacao = splitFreeText(swiRaw);
  }

  if (passosVerificacao.length === 0) {
    passosVerificacao = [
      'Consultar o padrão configurado no posto',
      'Verificar indicadores e evidências do processo',
      'Registrar o resultado da verificação',
    ];
  }

  const evidenciaEsperada = swiRaw ? extractSection(swiRaw, 'Evidência esperada') : null;

  const conformeBlock = swiRaw ? extractSection(swiRaw, 'Conforme quando') : null;
  const desvioBlock = swiRaw ? extractSection(swiRaw, 'Desvio quando') : null;

  let conformeQuando = conformeBlock ? parseSteps(conformeBlock) : [];
  if (conformeQuando.length === 0) {
    conformeQuando =
      template.swi_mode === 'checklist' && checklist.length > 0 && passosBlock
        ? checklist
        : checklist.length > 0
          ? checklist
          : [];
  }

  const indicatorCriteria = buildIndicatorCriteria(indicatorAssessment ?? null);
  conformeQuando = [...indicatorCriteria.conforme, ...conformeQuando];

  if (conformeQuando.length === 0) {
    conformeQuando = [
      'Processo auditado conforme o padrão',
      'Evidências disponíveis no gemba',
      'Indicador protegido dentro da meta',
    ];
  }

  let desvioQuando = desvioBlock ? parseSteps(desvioBlock) : [];
  desvioQuando = [...new Set([...indicatorCriteria.desvio, ...desvioQuando])];

  if (desvioQuando.length === 0) {
    desvioQuando = conformeQuando.map(invertConformityItem);
  }

  if (evidenciaEsperada) {
    desvioQuando.push('Evidência esperada ausente ou incompleta');
  }

  desvioQuando = [...new Set(desvioQuando)].slice(0, 8);

  return {
    objetivo,
    passosVerificacao,
    conformeQuando,
    desvioQuando,
    evidenciaEsperada,
  };
}
